import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, 
  TablePagination, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, 
  DialogActions, Button, TextField, CircularProgress, Alert, Grid, MenuItem, Autocomplete
} from '@mui/material';
import { Check, Close, Visibility, Edit, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import moment from 'moment';
import api from '../../api';
import MeetingEditor from './MeetingEditor';

const MeetingList = ({ viewType }) => {
  const [meetings, setMeetings] = useState([]);
  const [masters, setMasters] = useState({ purposes: [], depts: [], locations: [], employees: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters (Defaults to Current Month)
  const [filters, setFilters] = useState({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    status: '', department: '', purpose: ''
  });

  // Modals State
  const [actionModal, setActionModal] = useState({ open: false, type: '', meeting: null });
  const [viewModal, setViewModal] = useState({ open: false, meeting: null });
  const [editModal, setEditModal] = useState({ open: false, data: null });
  
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchMeetingsAndMasters = async () => {
    setLoading(true);
    try {
      const endpoint = viewType === 'ALL' ? '/meeting-views/all-meetings' : '/meeting-views/my-meetings';
      const [meetRes, purpRes, deptRes, locRes, empRes] = await Promise.all([
        api.get(endpoint),
        api.get('/meeting-views/purposes'),
        api.get('/meeting-views/departments'),
        api.get('/meeting-views/locations'),
        api.get('/employees')
      ]);
      setMeetings(meetRes.data);
      setMasters({ 
        purposes: purpRes.data, 
        depts: deptRes.data, 
        locations: locRes.data, 
        employees: empRes.data.data || empRes.data 
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to fetch data.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeetingsAndMasters(); }, [viewType]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(0);
  };

  // Apply Filters & Force Descending Order (Newest First)
  const filteredMeetings = meetings.filter(m => {
    const inDate = (!filters.startDate || m.meeting_date >= filters.startDate) && (!filters.endDate || m.meeting_date <= filters.endDate);
    const inStatus = filters.status ? m.status === filters.status : true;
    const inDept = filters.department ? m.department_id === filters.department : true;
    const inPurpose = filters.purpose ? m.purpose_id === filters.purpose : true;
    return inDate && inStatus && inDept && inPurpose;
  }).sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date)); // Sort Descending

  const handleActionSubmit = async () => {
    if (!notes.trim()) return setMessage({ type: 'error', text: 'Notes are mandatory.' });
    setSubmitting(true);
    try {
      const { type, meeting } = actionModal;
      // FIX: Pointing to `/meeting-views/` exactly matching the backend router prefix
      const endpoint = type === 'COMPLETE' 
        ? `/meeting-views/${meeting.meeting_id}/complete` 
        : `/meeting-views/${meeting.meeting_id}/cancel`;
      const payload = type === 'COMPLETE' ? { post_meeting_notes: notes } : { cancellation_reason: notes };
      
      await api.post(endpoint, payload);
      setMessage({ type: 'success', text: `Meeting ${type.toLowerCase()}d successfully.` });
      setActionModal({ open: false, type: '', meeting: null });
      setNotes('');
      fetchMeetingsAndMasters();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Action failed.' });
    } finally { setSubmitting(false); }
  };

  const openEditModal = (meeting) => {
    // Reconstruct attendee objects for Autocomplete
    const attendeeObjs = meeting.attendees.map(empId => masters.employees.find(e => e.emp_id === empId)).filter(Boolean);
    setEditModal({
      open: true,
      data: { ...meeting, attendees: attendeeObjs }
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (editModal.data.start_time >= editModal.data.end_time) return setMessage({ type: 'error', text: 'End time must be after start time.' });
    if (editModal.data.attendees.length === 0) return setMessage({ type: 'error', text: 'Select at least one attendee.' });
    
    setSubmitting(true);
    try {
      const payload = {
        ...editModal.data,
        attendees: editModal.data.attendees.map(a => a.emp_id),
        department_id: editModal.data.department_id || null
      };
      // FIX: Pointing to `/meeting-views/` prefix matching backend router
      await api.put(`/meeting-views/${editModal.data.meeting_id}/update`, payload);
      setMessage({ type: 'success', text: 'Meeting updated successfully.' });
      setEditModal({ open: false, data: null });
      fetchMeetingsAndMasters();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Update failed.' });
    } finally { setSubmitting(false); }
  };

  const handleExport = () => {
    if (!filteredMeetings.length) return;
    const exportData = filteredMeetings.map(m => ({
      "Meeting ID": m.meeting_id, "Subject": m.subject, "Status": m.status, 
      "Date": m.meeting_date, "Time": `${m.start_time} - ${m.end_time}`,
      "Duration (Mins)": m.duration_minutes, "Organizer": m.organizer_employee_id
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Meetings");
    XLSX.writeFile(wb, `Meetings_Export.xlsx`);
  };

  const getStatusColor = (status) => status === 'SCHEDULED' ? 'primary' : status === 'COMPLETED' ? 'success' : 'error';
  const getPurposeName = (id) => masters.purposes.find(p => p.id === id)?.purpose_name || id;
  const getDeptName = (id) => masters.depts.find(d => d.id === id)?.name || 'N/A';
  
  // Maps raw ID to Name safely
  const getEmployeeName = (empId) => {
    const emp = masters.employees.find(e => e.emp_id === empId);
    return emp ? `${emp.emp_id} - ${emp.first_name} ${emp.last_name || ''}`.trim() : empId;
  };

  return (
    <Box>
      {/* FILTER ROW (Fixed minWidths to prevent shrinking) */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From Date" name="startDate" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To Date" name="endDate" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField fullWidth size="small" select label="Status" name="status" value={filters.status} onChange={handleFilterChange} sx={{ minWidth: 120 }}>
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth size="small" select label="Department" name="department" value={filters.department} onChange={handleFilterChange} sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {masters.depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth size="small" select label="Purpose" name="purpose" value={filters.purpose} onChange={handleFilterChange} sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>All Purposes</em></MenuItem>
              {masters.purposes.map(p => <MenuItem key={p.id} value={p.id}>{p.purpose_name}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* DATA TABLE */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" color="primary">{viewType === 'ALL' ? 'All Organization Meetings' : 'My Meetings'}</Typography>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport}>Export Excel</Button>
        </Box>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({type:'', text:''})}>{message.text}</Alert>}

        {loading ? <CircularProgress sx={{ display: 'block', m: 'auto' }} /> : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Meeting ID</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Duration</TableCell>
                  {/* Show Organizer Column ONLY in ALL view */}
                  {viewType === 'ALL' && <TableCell>Organizer</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMeetings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{row.meeting_id}</TableCell>
                    <TableCell>{row.meeting_date}<br/><Typography variant="caption">{moment(row.start_time, 'HH:mm:ss').format('hh:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('hh:mm A')}</Typography></TableCell>
                    <TableCell>{row.subject}<br/><Typography variant="caption" color="textSecondary">{getPurposeName(row.purpose_id)}</Typography></TableCell>
                    <TableCell>{row.duration_minutes} Mins</TableCell>
                    
                    {/* Render Organizer Data if ALL view */}
                    {viewType === 'ALL' && (
                      <TableCell>
                        <Typography variant="body2">{getEmployeeName(row.organizer_employee_id)}</Typography>
                      </TableCell>
                    )}

                    <TableCell><Chip size="small" label={row.status} color={getStatusColor(row.status)} /></TableCell>
                    <TableCell align="center">
                      {row.status === 'SCHEDULED' ? (
                        <>
                          <Tooltip title="Edit Meeting"><IconButton size="small" color="primary" onClick={() => openEditModal(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Complete"><IconButton size="small" color="success" onClick={() => setActionModal({ open: true, type: 'COMPLETE', meeting: row })}><Check fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => setActionModal({ open: true, type: 'CANCEL', meeting: row })}><Close fontSize="small" /></IconButton></Tooltip>
                        </>
                      ) : (
                        <Tooltip title="View Details"><IconButton size="small" color="info" onClick={() => setViewModal({ open: true, meeting: row })}><Visibility fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredMeetings.length === 0 && <TableRow><TableCell colSpan={viewType === 'ALL' ? 7 : 6} align="center">No meetings found.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination component="div" count={filteredMeetings.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </Paper>

      {/* VIEW DETAILS MODAL */}
      <Dialog open={viewModal.open} onClose={() => setViewModal({ open: false, meeting: null })} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff' }}>Meeting Details - {viewModal.meeting?.meeting_id}</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fbfbfb' }}>
          {viewModal.meeting && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><Typography variant="subtitle2" color="textSecondary">Subject</Typography><Typography variant="body1" fontWeight="bold">{viewModal.meeting.subject}</Typography></Grid>
              <Grid item xs={12} md={3}><Typography variant="subtitle2" color="textSecondary">Status</Typography><Chip size="small" label={viewModal.meeting.status} color={getStatusColor(viewModal.meeting.status)} sx={{ mt: 0.5 }} /></Grid>
              <Grid item xs={12} md={4}><Typography variant="subtitle2" color="textSecondary">Date & Time</Typography><Typography variant="body1">{viewModal.meeting.meeting_date}</Typography><Typography variant="body2" color="textSecondary">{viewModal.meeting.start_time} to {viewModal.meeting.end_time}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="subtitle2" color="textSecondary">Purpose</Typography><Typography variant="body1">{getPurposeName(viewModal.meeting.purpose_id)}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="subtitle2" color="textSecondary">Department</Typography><Typography variant="body1">{getDeptName(viewModal.meeting.department_id)}</Typography></Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Attendees ({viewModal.meeting.attendees?.length || 0})</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Rendering Actual Names + ID */}
                  {viewModal.meeting.attendees?.map(empId => (
                    <Chip key={empId} label={getEmployeeName(empId)} size="small" variant="outlined" color="primary" />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Meeting Agenda / Notes</Typography>
                  <Box dangerouslySetInnerHTML={{ __html: viewModal.meeting.meeting_notes }} sx={{ '& p': { m: 0 } }} />
                </Paper>
              </Grid>
              
              {viewModal.meeting.status === 'COMPLETED' && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #c8e6c9', bgcolor: '#f1f8e9', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>Minutes of Meeting (MoM)</Typography>
                    <Box dangerouslySetInnerHTML={{ __html: viewModal.meeting.post_meeting_notes }} sx={{ '& p': { m: 0 } }} />
                  </Paper>
                </Grid>
              )}
              {viewModal.meeting.status === 'CANCELLED' && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #ffcdd2', bgcolor: '#ffebee', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="error.main" gutterBottom>Cancellation Reason</Typography>
                    <Typography variant="body2">{viewModal.meeting.cancellation_reason}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setViewModal({ open: false, meeting: null })} variant="contained">Close</Button></DialogActions>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editModal.open} onClose={() => !submitting && setEditModal({ open: false, data: null })} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff' }}>Edit Scheduled Meeting</DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent dividers>
            {editModal.data && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth required label="Subject" value={editModal.data.subject} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, subject: e.target.value }})} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth select required label="Purpose" value={editModal.data.purpose_id} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, purpose_id: e.target.value }})}>
                    {masters.purposes.map(p => <MenuItem key={p.id} value={p.id}>{p.purpose_name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth select required label="Location" value={editModal.data.location_id} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, location_id: e.target.value }})}>
                    {masters.locations.map(l => <MenuItem key={l.id} value={l.id}>{l.location_name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth select label="Department" value={editModal.data.department_id || ''} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, department_id: e.target.value }})}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {masters.depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required type="date" label="Date" InputLabelProps={{ shrink: true }} value={editModal.data.meeting_date} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, meeting_date: e.target.value }})} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required type="time" label="Start Time" InputLabelProps={{ shrink: true }} value={editModal.data.start_time} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, start_time: e.target.value }})} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required type="time" label="End Time" InputLabelProps={{ shrink: true }} value={editModal.data.end_time} onChange={(e) => setEditModal({ open: true, data: { ...editModal.data, end_time: e.target.value }})} />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple options={masters.employees}
                    getOptionLabel={(o) => `${o.emp_id} - ${o.first_name} ${o.last_name || ''}`.trim()}
                    isOptionEqualToValue={(o, v) => o.emp_id === v.emp_id}
                    value={editModal.data.attendees}
                    onChange={(e, val) => setEditModal({ open: true, data: { ...editModal.data, attendees: val }})}
                    renderTags={(value, getTagProps) => value.map((option, index) => (
                      <Chip variant="outlined" size="small" label={option.first_name} {...getTagProps({ index })} key={option.emp_id} />
                    ))}
                    renderInput={(params) => <TextField {...params} label="Attendees" required={editModal.data.attendees.length === 0} />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MeetingEditor label="Agenda / Notes" value={editModal.data.meeting_notes} onChange={(content) => setEditModal({ open: true, data: { ...editModal.data, meeting_notes: content }})} required />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditModal({ open: false, data: null })} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ACTION MODAL (Complete / Cancel) */}
      <Dialog open={actionModal.open} onClose={() => setActionModal({ open: false, type: '', meeting: null })} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: actionModal.type === 'COMPLETE' ? 'success.main' : 'error.main', color: '#fff' }}>
          {actionModal.type === 'COMPLETE' ? 'Complete Meeting' : 'Cancel Meeting'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>Meeting: {actionModal.meeting?.subject} ({actionModal.meeting?.meeting_id})</Typography>
          {actionModal.type === 'COMPLETE' ? (
            <Box sx={{ mt: 2 }}>
              <MeetingEditor label="Minutes of Meeting (MoM) / Outcome" value={notes} onChange={setNotes} required />
            </Box>
          ) : (
            <TextField fullWidth required multiline rows={4} sx={{ mt: 2 }} label="Cancellation Reason" value={notes} onChange={(e) => setNotes(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionModal({ open: false, type: '', meeting: null })} disabled={submitting}>Go Back</Button>
          <Button onClick={handleActionSubmit} variant="contained" color={actionModal.type === 'COMPLETE' ? 'success' : 'error'} disabled={submitting}>
            {submitting ? 'Processing...' : `Confirm ${actionModal.type}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingList;